<template>
  <div :id="id" class="note" :class="classObject" :style="styles">
    <div
      v-touch="onTouch"
      class="wrapper"
      @contextmenu="onContextMenu"
      @mousedown="onMouseDown"
      @mouseup="onMouseUp"
    >
      <rich-text-editor
        ref="contentEditor"
        class="container"
        :content="note.content"
        :editable="canEdit"
        :activate-on-double-click="true"
        :extension-options="extensionOptions"
        @focus="onFocusContent"
        @blur="onBlurContent"
        @update="onUpdateContent"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import _ from 'lodash';

import nodeOnTouch from '@/components/mixins/nodeOnTouch.js';
import jspSingleton from '@/lib/jspSingleton.js';
import { useProtect } from '@/composables/useProtect.js';
import { SYS_PERMISSIONS } from '@/constants.js';
import misc from '../mixins/misc.js';

import RichTextEditor from '../all/RichText/RichTextEditor.vue';
import Rect from '../../rect.js';
import Point from '../../point.js';
import updater from '../../vuex/project/updater.js';
import { roundToNearestMultiple } from '../../utils.js';

export default {
  components: {
    'rich-text-editor': RichTextEditor,
  },

  mixins: [misc, nodeOnTouch],

  props: {
    id: {
      type: String,
      default: null,
    },
    virtualSelected: {
      type: Boolean,
      default: false,
    },
  },

  setup() {
    const { hasPermission: canEdit } = useProtect({
      permissions: [SYS_PERMISSIONS.SYS_PROJECTS_EDIT],
      roles: ['editor'],
    });

    return {
      canEdit,
    };
  },

  data() {
    return {
      extensionOptions: {
        hardbreak: true,
        bold: true,
        italic: true,
        underline: true,
        horizontalRule: false,
        customLink: true,
        history: true,
        placeholder: {
          text: 'Double click to add content…',
        },
        dropEventHandler: true,
        customMention: true,
      },
      resizeProps: {
        start: this.onStartResize,
        resize: this.onResize,
        stop: this.onEndResize,
        handles: 'nw,ne,se,sw,n,w,s,e',
        containment: '.panzoom',
      },
      showSelectionsTimer: null,
      minHeight: '101px',
      resizeObserver: null,
    };
  },

  computed: {
    ...mapGetters('project/data/notes', ['getContainedNodes', 'getRect']),
    ...mapGetters('project/data/notes', { getNote: 'getNode' }),
    ...mapGetters('project/data/elements', { getElement: 'getNode' }),
    ...mapGetters('project/data/jumpers', { getJumper: 'getNode' }),
    ...mapGetters('project/data/branches', { getBranch: 'getNode' }),
    ...mapGetters('project/data/comments', { getComment: 'getNode' }),
    ...mapGetters('application/channels', ['getCollaborators']),
    ...mapGetters('project/config/notes', ['getIsSelected']),
    ...mapGetters('project/config/boards', ['getTransform', 'getOpen']),
    ...mapGetters('project/config', ['getGrid', 'getIsMultiselection']),

    /**
     * Return the note
     * @returns {Object}    The note
     */
    note() {
      return this.getNote(this.id);
    },

    /**
     * Return the css styles for positioning
     * @returns {Object}    The { left, top, width, height, min-height, --user-selected-color } styles
     */
    styles() {
      const { x, y, width, height, autoHeight } = this.note || {
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        autoHeight: false,
      };
      return {
        left: `${x}px`,
        top: `${y}px`,
        width: `${width}px`,
        height: autoHeight ? 'auto' : `${height}px`,
        'min-height': autoHeight ? this.minHeight : 'unset',
        '--user-selected-color': this.selectedByCollaborator?.color,
      };
    },

    /**
     * Return the collaboarator that has selected this
     */
    selectedByCollaborator() {
      const collaborators = this.getCollaborators.filter(
        c =>
          c.selections &&
          c.selections.notes &&
          c.selections.notes.includes(this.id)
      );
      if (collaborators.length > 0) return collaborators[0];
      return null;
    },

    /**
     * Return the current board transform
     * @returns {Object}    The board transform
     */
    transform() {
      return this.getTransform(this.getOpen);
    },

    /**
     * Return whether the current note is selected
     * @returns {Boolean}   Whether it is selected
     */
    isSelected() {
      return this.getIsSelected(this.id);
    },

    classObject() {
      return {
        [this.note.theme]: true,
        'jtk-drag-selected': this.isSelected,
        'ui-resizable': true,
        'ui-resizable-disabled': !this.showHandlers,
        'user-selected': this.selectedByCollaborator !== null,
        'virtual-selected': this.virtualSelected,
      };
    },

    /**
     * Enable the connection handlers.
     */
    showHandlers() {
      return this.isSelected && !this.getIsMultiselection;
    },
  },

  watch: {
    /**
     * Watch selection change to inform jsplumb and enable-disable resizable.
     */
    isSelected(newVal, oldVal) {
      console.log('selected watcher for:', this.id);
      if (!this.canEdit) return;
      if (newVal) {
        // Add it to jsplumb selection
        jspSingleton.instance.addToDragSelection(this.$el);
      } else {
        jspSingleton.instance.removeFromDragSelection(this.$el);
      }
    },

    /**
     * Watch show handlers change for changing resizable state
     */
    showHandlers(newVal, oldVal) {
      if (!this.canEdit) return;
      if (newVal) {
        // Make element resizable
        $(this.$el).resizable(this.resizeProps);
        // Setup event listeners for auto resizing
        $(this.$el)
          .find('.ui-resizable-handle')
          .on('dblclick', () => {
            this.autoresizeHeight();
          });
      } else {
        // Remove event listeners for auto resizing
        $(this.$el).find('.ui-resizable-handle').off('dblclick');
        // Make element unresizable
        // $(this.$el).resizable('option', 'disabled', true);
        // Make element unresizable
        $(this.$el).resizable('destroy');
      }
    },

    /**
     * Watch when something is being dragged over the note.
     */
    isDragging(newVal, oldVal) {
      if (!newVal) {
        this.dragEnter = false;
      }
    },
  },

  /**
   * Lifecycle hook, the note has been mounted
   */
  mounted() {
    // Make note draggable and resizable only if it is editable
    if (!this.canEdit) return;

    // Add it to the managed list to make it draggable
    jspSingleton.instance.manage(this.$el);

    // Make note resizable
    // $(this.$el).resizable(this.resizeProps);
    // $(this.$el).resizable('option', 'disabled', true);

    if (this.isSelected) {
      jspSingleton.instance.addToDragSelection(this.$el);
      $(this.$el).resizable(this.resizeProps);
      // $(this.$el).resizable('enable');
    }

    this.resizeObserver = new ResizeObserver(() => {
      this.recalcAnchorRect();
    });
  },

  /**
   * Lifecycle hook, the note is about to be unmounted
   */
  beforeUnmount() {
    // Remove it from jsplumb's list of managed elements
    jspSingleton.instance.unmanage(this.$el, false);
  },

  methods: {
    ...mapActions('application/contextToolbar', [
      'setAnchor',
      'recalcAnchorRect',
    ]),
    ...mapActions('project/config', ['deselectAll', 'deleteSelections']),
    ...mapActions('project/data/notes', { updateNote: 'customUpdate' }),
    ...mapActions('project/data/elements', { updateElement: 'customUpdate' }),
    ...mapActions('project/data/jumpers', { updateJumper: 'customUpdate' }),
    ...mapActions('project/data/branches', { updateBranch: 'customUpdate' }),
    ...mapActions('project/data/comments', { updateComment: 'customUpdate' }),
    ...mapActions('project/config/notes', {
      selectNote: 'select',
      deselectNote: 'deselect',
    }),
    ...mapActions('application/contextMenu', [
      'createItem',
      'setCalcPosition',
      'deleteMenu',
      'setOnDeleteMenu',
    ]),
    ...mapMutations('application/appStates', ['setShowSelections']),

    /**
     * Autoresize note to content height
     */
    autoresizeHeight() {
      console.log('BoardNote::autoresizeHeight()');
      // Set element height to auto
      this.$el.style['min-height'] = this.minHeight;
      this.$el.style.height = 'auto';

      // Update element with the new height from client
      this.updateNote({
        id: this.id,
        props: {
          height: this.$el.clientHeight,
          autoHeight: true,
        },
        apply: true,
      });

      // Repaint element connections
      this.$nextTick(() => {
        jspSingleton.instance?.revalidate(this.$el);
      });
    },

    /**
     * Event listener - mouse up event on note
     */
    onMouseUp(event) {
      const { offsetX, offsetY, target, button } = event;
      // Ignore mouse up if occured on a mention or a hyperlink
      if (target.closest('.mention, a')) {
        return;
      }

      // Ignore mouse up on scrollbar and scroll
      const { clientWidth, clientHeight } = target;
      if (
        target.classList.contains('editor') &&
        (offsetX > clientWidth || offsetY > clientHeight)
      ) {
        return;
      }

      const isDragged = jspSingleton.instance.hasClass(this.$el, 'jtk-drag');
      // Check if we are after a drag or after a single click
      if (!isDragged && this.canEdit && button !== 1) {
        if (!this.$el.contains(document.activeElement)) {
          this.removeAllRanges();
        }
        // When Ctrl or Shift pressed toggle selection
        if (event.ctrlKey || event.shiftKey || event.metaKey) {
          if (!this.isSelected) {
            this.selectNote({ id: this.id, apply: true });
          } else {
            this.deselectNote({ id: this.id, apply: true });
          }
        } else if (!this.isSelected) {
          // Clear any selections and select only this note
          this.deselectAll({ apply: false });
          // Clear the selections from jsplumb
          jspSingleton.instance.clearDragSelection();
          this.selectNote({ id: this.id, apply: true });
        }
      }
    },

    /**
     * Event listener - context menu event
     */
    onContextMenu(event) {
      console.log('Note RIGHT CLICK (on context menu)');
      // Do nothing if user cannot edit
      if (!this.canEdit) return;
      const { contentEditor } = this.$refs;
      // Do nothing if inside text field (will be handled by the editor)
      if (
        contentEditor.$el.contains(event.target) &&
        contentEditor.isEditMode()
      ) {
        return;
      }

      event.preventDefault();
      // Select the note
      if (!this.isSelected) {
        this.deselectAll({ apply: false });
        // Clear the selections from jsplumb
        jspSingleton.instance?.clearDragSelection();
        this.selectNote({ id: this.id, apply: true });
      }
      const x = event.clientX || event.changedPointers[0].clientX; // Get alternate clientX/clientY from touch event
      const y = event.clientY || event.changedPointers[0].clientY;
      this.createContextMenu(x, y);
    },

    /**
     * Create a context menu for this component
     * @param {Number} x  The x client coordinate
     * @param {Number} y  The y client coordinate
     */
    async createContextMenu(x, y) {
      this.createItem({
        name: null, // Color
        icon: null,
        commands: {
          doAction: () => {},
          isActive: () => {
            return true;
          },
          isEnabled: () => {
            return true;
          },
        },
        addon: {
          component: 'color-picker',
          props: {
            selectedColor: this.note.theme,
            onChangeColor: () => {
              this.setShowSelections({ value: false });
              clearTimeout(this.showSelectionsTimer);
              this.showSelectionsTimer = setTimeout(
                () => this.setShowSelections({ value: true }),
                1600
              );
            },
          },
        },
      });

      this.createItem({
        name: 'Delete selection',
        icon: null,
        commands: {
          doAction: () => {
            this.deleteSelections({ apply: true });
            this.deleteMenu();
          },
          isActive: () => {
            return true;
          },
          isEnabled: () => {
            return true;
          },
        },
        submenu: null,
      });

      this.setCalcPosition({
        calcPosition: () => {
          return { left: x, top: y };
        },
      });

      this.setOnDeleteMenu({
        onDeleteMenu: () => {
          clearTimeout(this.showSelectionsTimer);
          this.setShowSelections({ value: true });
        },
      });
    },

    /**
     * Enable or disable the resizeObserver for the note
     * @param {Boolean} enabled If the observer is enabled
     */
    observeHeight(enabled = true) {
      if (enabled) {
        this.resizeObserver.observe(this.$el);
      } else {
        this.resizeObserver.unobserve(this.$el);
      }
    },

    /**
     * Event listener - the content gains the focus (custom richEditor event)
     */
    onFocusContent(event) {
      if (jspSingleton.instance) {
        jspSingleton.instance.setDraggable(this.$el, false);
      }

      this.setAnchor({
        anchor: () => {
          const labelRect = this.$el.getBoundingClientRect();
          const rect = new Rect(
            labelRect.left,
            labelRect.right,
            labelRect.top,
            labelRect.bottom
          );
          const parentRect = this.$parent.$el.getBoundingClientRect();
          const parentPt = new Point(parentRect.left, parentRect.top);
          rect.offsetRect(parentPt);
          return rect;
        },
      });

      // Enable the resizeObserver for this note
      this.observeHeight();
    },

    /**
     * Event listener - the content loses the focus (custom richEditor event)
     */
    onBlurContent(event) {
      this.updateNote({
        id: this.id,
        props: {
          content: this.$refs.contentEditor.getContent(),
          height: this.$el.clientHeight,
        },
        apply: true,
      });

      // Re-enable note dragging
      jspSingleton.instance?.setDraggable(this.$el, true);

      // Disable the resizeObserver for this note
      this.observeHeight(false);
    },

    /**
     * Event listener - the content is updated (custom richEditor event)
     */
    onUpdateContent(content) {
      // this.updateNote({
      //   id: this.id,
      //   props: {
      //     content,
      //   },
      //   apply: true,
      // });
    },

    /**
     * Event hanlder: note mouse down event.
     * @param {MouseEvent} event
     */
    onMouseDown(event) {
      // On middle-click do not start drag
      if (event.button === 1) {
        event.preventDefault();
      }
    },

    /**
     * Event listener - Note starts being resized (jquery ui event)
     */
    onStartResize(event, ui) {
      this.$el.style.removeProperty('min-height');
      this.removeAllRanges();
    },

    /**
     * Event listener - Element is being resized (jquery ui event)
     */
    onResize(event, ui) {
      console.log('onResize for note');
      // The scale
      const { scale } = this.transform;

      // The new size and position
      let { width, height } = ui.size;
      let { left, top } = ui.position;

      // The old size and position
      const { width: oldWidth, height: oldHeight } = ui.originalSize;
      const { left: oldLeft, top: oldTop } = ui.originalPosition;

      // Our minimum values
      const minHeight = 42;
      const minWidth = 122;

      // Compute right and bottom values
      const right = left + width;
      const bottom = top + height;

      const oldRight = oldLeft + oldWidth;
      const oldBottom = oldTop + oldHeight;

      const scaledWidth = oldWidth + (width - oldWidth) / scale;
      const scaledLeft = oldLeft + (left - oldLeft) / scale;
      const scaledRight = left + scaledWidth;
      const scaledHeight = oldHeight + (height - oldHeight) / scale;
      const scaledTop = oldTop + (top - oldTop) / scale;
      const scaledBottom = top + scaledHeight;

      // Account for grid snapping
      const { snap, size } = this.getGrid;
      if (snap) {
        if (left !== oldLeft) {
          left = Math.max(roundToNearestMultiple(scaledLeft, size), 0);
          width = oldRight - left;
          // Adjustment for grid lines
          width += 1;
        } else if (right !== oldRight) {
          width = roundToNearestMultiple(scaledRight, size) - left;
          // Adjustment for grid lines
          width += 1;
        }
        if (top !== oldTop) {
          top = Math.max(roundToNearestMultiple(scaledTop, size), 0);
          height = oldBottom - top;
          // Adjustment for grid lines
          height += 1;
        } else if (bottom !== oldBottom) {
          height = roundToNearestMultiple(scaledBottom, size) - top;
          // Adjustment for grid lines
          height += 1;
        }
      } else {
        if (left !== oldLeft) {
          left = Math.max(scaledLeft, 0);
          width = oldRight - left;
        } else {
          width = scaledWidth;
        }
        if (top !== oldTop) {
          top = Math.max(scaledTop, 0);
          height = oldBottom - top;
        } else {
          height = scaledHeight;
        }
      }

      // Constrain size and position
      const dw = width - minWidth;
      const dh = height - minHeight;
      width = Math.max(width, minWidth);
      height = Math.max(height, minHeight);
      width = Math.min(width, 20000 - left);
      height = Math.min(height, 20000 - top);

      if (width === minWidth && left > oldLeft) {
        left = Math.max(left + dw, 0);
      }
      if (height === minHeight && top > oldTop) {
        top = Math.max(top + dh, 0);
      }

      ui.size.width = width;
      ui.size.height = height;
      ui.position.left = left;
      ui.position.top = top;

      ui.helper.width(ui.size.width);
      ui.helper.height(ui.size.height);
      $(ui.helper).css({
        left: `${ui.position.left}px`,
        top: `${ui.position.top}px`,
      });
      // jspSingleton.instance.revalidate(ui.helper);
    },

    /**
     * Event listener - Note finished being resized (jquery ui event)
     */
    onEndResize(event, ui) {
      this.updateNote({
        id: this.id,
        props: {
          width: ui.size.width,
          height: ui.size.height,
          autoHeight: false,
          x: ui.position.left,
          y: ui.position.top,
        },
        apply: true,
      });
    },
  },
};
</script>

<style scoped lang="scss">
.note {
  position: absolute;
  box-sizing: border-box;
  font-size: 16px;
  color: #bcbcbc;
  width: 250px;
  display: flex;
  // box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  border-radius: 4px;
}

.note .container {
  height: 100%;
  display: flex;
  overflow: hidden;
}

// .note.jtk-drag-selected {
//   z-index: 1;
// }

.note .wrapper {
  display: flex;
  width: 100%;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  position: relative;
  border-radius: 8px;
  padding: 14px 20px;
}

/* NOTE THEMES */

.note.default {
  background-color: rgba(53, 61, 62, 0.6);
}

.note.red {
  background-color: rgba(95, 22, 0, 0.6);
}

.note.blue {
  background-color: rgba(16, 45, 163, 0.6);
}

.note.purple {
  background-color: rgba(84, 0, 147, 0.6);
}

.note.cyan {
  background-color: rgba(0, 101, 87, 0.6);
}

.note.green {
  background-color: rgba(14, 76, 22, 0.6);
}

.note.orange {
  background-color: rgba(133, 49, 0, 0.6);
}

.note.brown {
  background-color: rgba(86, 52, 0, 0.6);
}

.note.gold {
  background-color: rgba(93, 90, 0, 0.6);
}

.note.moss {
  background-color: rgba(56, 99, 0, 0.6);
}

.note.pink {
  background-color: rgba(105, 0, 63, 0.6);
}

.note.lightBlue {
  background-color: rgba(0, 81, 134, 0.6);
}

.note .content {
  background-color: transparent;
  font-size: 14px;
  line-height: 1.5;
  flex: 1;
  display: block;
  text-align: left;
  text-overflow: ellipsis;
  min-height: 22px;
  min-width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  cursor: default;
}
</style>

<style lang="scss">
.note .content .mention-wrapper {
  display: inline-block;
}

.note .content .mention {
  color: #db841e;
  display: inline-block;
  user-select: text;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
}

.note .content .mention.mention-board {
  color: #dbab1e;
}

// While dragging do not fire any clicking on mentions or links
.note.jtk-drag .mention,
.note.jtk-drag a {
  pointer-events: none;
}
</style>
